import { useState, useRef, useEffect } from "react";
import { FiMessageCircle, FiX } from "react-icons/fi";
import "./PortfolioChatbot.css";

// Predefined questions — the only way users can interact
const questions = [
  "What are your main skills?",
  "Tell me about your WordPress experience",
  "Do you know Laravel?",
  "What services do you offer?",
  "Do you work with AI integrations?",
  "What cloud tools do you use?",
  "Tell me about your projects",
  "What roles are you suitable for?",
];

// Initial greeting from the assistant
const INTRO_MESSAGE = {
  role: "assistant",
  text: "Hi! Ask me about my skills, projects, experience, or services.",
};

const PortfolioChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([INTRO_MESSAGE]);
  const [loading, setLoading] = useState(false);

  // Ref for auto-scrolling to latest message
  const messagesEndRef = useRef(null);

  // Auto scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Send a predefined question to the backend
  const sendQuestion = async (question) => {
    if (loading) return;

    // Add user's selected question to chat
    setMessages((prev) => [...prev, { role: "user", text: question }]);
    setLoading(true);

    try {
      const response = await fetch(
        "https://portfolio-chatbot-q0tg.onrender.com/api/chat",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: question }),
        }
      );

      if (!response.ok) throw new Error("Server error");

      const data = await response.json();

      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: data.reply || "Sorry, I couldn't process that." },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: "Sorry, something went wrong. Please try again later.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating toggle button */}
      <button
        className={`chatbot-toggle ${isOpen ? "chatbot-toggle--active" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        {isOpen ? <FiX size={22} /> : <FiMessageCircle size={22} />}
      </button>

      {/* Chat window */}
      {isOpen && (
        <div className="chatbot">
          {/* Header */}
          <div className="chatbot__header">
            <div className="chatbot__header-info">
              <div className="chatbot__avatar">A</div>
              <div>
                <strong>Ask My Portfolio</strong>
                <span>Guided Assistant</span>
              </div>
            </div>
            <button
              className="chatbot__close"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              <FiX size={18} />
            </button>
          </div>

          {/* Messages area */}
          <div className="chatbot__messages">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`chatbot__bubble chatbot__bubble--${msg.role}`}
              >
                {msg.text}
              </div>
            ))}

            {/* Typing indicator */}
            {loading && (
              <div className="chatbot__bubble chatbot__bubble--assistant chatbot__typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Predefined question buttons — always visible at the bottom */}
          <div className="chatbot__questions">
            <p className="chatbot__questions-label">Select a question:</p>
            <div className="chatbot__questions-list">
              {questions.map((q, i) => (
                <button
                  key={i}
                  className="chatbot__question-btn"
                  onClick={() => sendQuestion(q)}
                  disabled={loading}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PortfolioChatbot;
